import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaDownload } from 'react-icons/fa';
import { Container } from '../../styles/components.js';
import ErrorText from '../ErrorText';

const ContactSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.background};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
  letter-spacing: -0.03em;
  span {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.7;
  margin-bottom: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 2.5rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

/* ─── Info Panel ─────────────────────────────────────────────────── */
const InfoPanel = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 24px;
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 2.25rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  position: relative;
  overflow: hidden;
`;

const InfoTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;
`;

const InfoSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textGray};
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 0;
`;

const InfoDetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const InfoIconBox = styled.div`
  width: 44px;
  height: 44px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const InfoText = styled.div``;
const InfoLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 2px;
`;
const InfoValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textDark};
`;

const CTABox = styled.div`
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CTATitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 1.25rem;
`;

const CTAButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CTALink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.8rem 1.25rem;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDark};
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Space Grotesk', sans-serif;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    background: ${({ theme }) => theme.colors.primary}08;
    transform: translateX(4px);
    box-shadow: ${({ theme }) => theme.shadows.glowSm};
  }

  .icon {
    width: 32px;
    height: 32px;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    color: ${({ theme }) => theme.colors.primary};
  }

  .label { flex: 1; }
  .arrow { font-size: 0.9rem; opacity: 0.5; }
`;

/* ─── Form Panel ─────────────────────────────────────────────────── */
const FormPanel = styled(motion.form)`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 24px;
  padding: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.card};
`;

const FormTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;
`;

const FormSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const FloatingLabel = styled.label`
  position: absolute;
  top: ${({ $lifted }) => ($lifted ? '-9px' : '15px')};
  left: 16px;
  font-size: ${({ $lifted }) => ($lifted ? '0.7rem' : '0.9rem')};
  font-weight: ${({ $lifted }) => ($lifted ? '700' : '500')};
  color: ${({ $lifted, theme }) => $lifted ? theme.colors.primary : theme.colors.textGray};
  background: ${({ $lifted, theme }) => $lifted ? theme.colors.surfaceAlt : 'transparent'};
  padding: ${({ $lifted }) => $lifted ? '0 6px' : '0'};
  pointer-events: none;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  letter-spacing: ${({ $lifted }) => $lifted ? '0.04em' : '0'};
  text-transform: ${({ $lifted }) => $lifted ? 'uppercase' : 'none'};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid ${({ $focused, theme }) => $focused ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textDark};
  outline: none;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Inter', sans-serif;

  &:focus {
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid ${({ $focused, theme }) => $focused ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textDark};
  outline: none;
  resize: vertical;
  min-height: 140px;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Inter', sans-serif;

  &:focus {
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 99px;
  background: ${({ theme }) => theme.colors.gradient};
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  font-family: 'Space Grotesk', sans-serif;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: ${({ theme }) => theme.shadows.glow};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    box-shadow: ${({ theme }) => theme.shadows.glowLg};
    transform: translateY(-2px);
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessBox = styled(motion.div)`
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  color: #fff;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.2);

  .check { font-size: 3rem; margin-bottom: 0.75rem; }
  h4 { color: #fff; font-size: 1.25rem; margin-bottom: 0.5rem; font-weight: 800; }
  p { color: rgba(255,255,255,0.9); margin: 0; font-size: 0.9rem; }
`;

const FloatingInput = ({ label, name, type = 'text', value, onChange, placeholder, required }) => {
  const [focused, setFocused] = useState(false);
  const lifted = focused || !!value;
  return (
    <FormGroup>
      <FloatingLabel $lifted={lifted}>{label}</FloatingLabel>
      <Input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        placeholder={focused ? placeholder : ''}
        $focused={focused}
      />
    </FormGroup>
  );
};

const FloatingTextarea = ({ label, name, value, onChange, placeholder, required }) => {
  const [focused, setFocused] = useState(false);
  const lifted = focused || !!value;
  return (
    <FormGroup>
      <FloatingLabel $lifted={lifted}>{label}</FloatingLabel>
      <TextArea
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        placeholder={focused ? placeholder : ''}
        $focused={focused}
      />
    </FormGroup>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required.';
    if (!formData.email.trim()) e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S/.test(formData.email)) e.email = 'Email is invalid.';
    if (!formData.subject.trim()) e.subject = 'Subject is required.';
    if (!formData.message.trim()) e.message = 'Message is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    emailjs.send(
      'service_dwgd8ij', 'template_85dlxyq',
      { from_name: formData.name, from_email: formData.email, subject: formData.subject, message: formData.message, title: formData.subject, name: formData.name },
      'sf26lv55HhJocV4_K'
    ).then(() => {
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 6000);
    }).catch((err) => {
      alert(`Failed to send message: ${err.text || JSON.stringify(err)}`);
    }).finally(() => setIsSubmitting(false));
  };

  return (
    <ContactSection id="contact">
      <Container>
        <SectionHeader>
          <Label>06 / CONTACT</Label>
          <Title>Let's Work <span>Together</span></Title>
          <Desc>Have a project in mind? Let's build something great with React Native.</Desc>
        </SectionHeader>

        <ContactGrid>
          {/* ─── Info Panel ─── */}
          <InfoPanel
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div>
              <InfoTitle>Get In Touch</InfoTitle>
              <InfoSubtitle>
                Available for freelance projects, full-time roles, and app collaborations worldwide.
              </InfoSubtitle>
            </div>

            <InfoDetailList>
              <InfoDetail>
                <InfoIconBox>📍</InfoIconBox>
                <InfoText>
                  <InfoLabel>Location</InfoLabel>
                  <InfoValue>Islamabad, Bahria Enclave, Pakistan</InfoValue>
                </InfoText>
              </InfoDetail>
              <InfoDetail>
                <InfoIconBox>📧</InfoIconBox>
                <InfoText>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>ahmedtausef98@gmail.com</InfoValue>
                </InfoText>
              </InfoDetail>
              <InfoDetail>
                <InfoIconBox>📱</InfoIconBox>
                <InfoText>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoValue>+92 314 5976605</InfoValue>
                </InfoText>
              </InfoDetail>
            </InfoDetailList>

            <CTABox>
              <CTATitle>Connect With Me</CTATitle>
              <CTAButtons>
                <CTALink href="https://github.com/TauseefAlpha" target="_blank" rel="noopener noreferrer">
                  <span className="icon">
                    <FaGithub />
                  </span>
                  <span className="label">GitHub Profile</span>
                  <span className="arrow">→</span>
                </CTALink>
                <CTALink href="https://www.linkedin.com/in/tauseef-ahmad-a2107523b/" target="_blank" rel="noopener noreferrer">
                  <span className="icon">
                    <FaLinkedin />
                  </span>
                  <span className="label">LinkedIn Profile</span>
                  <span className="arrow">→</span>
                </CTALink>
                <CTALink href="/ReactNativeCv.pdf" download>
                  <span className="icon">
                    <FaDownload />
                  </span>
                  <span className="label">Download Resume</span>
                  <span className="arrow">⬇</span>
                </CTALink>
              </CTAButtons>
            </CTABox>
          </InfoPanel>

          {/* ─── Form Panel ─── */}
          <FormPanel
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={handleSubmit}
          >
            <FormTitle>Send a Message</FormTitle>
            <FormSubtitle>I'll get back to you within 24 hours.</FormSubtitle>

            {isSubmitted ? (
              <SuccessBox
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              >
                <div className="check">✅</div>
                <h4>Message Sent!</h4>
                <p>Thanks for reaching out. I'll respond shortly.</p>
              </SuccessBox>
            ) : (
              <>
                <FloatingInput label="Your Name" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required />
                {errors.name && <ErrorText>{errors.name}</ErrorText>}

                <FloatingInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}

                <FloatingInput label="Subject" name="subject" value={formData.subject} onChange={handleChange} placeholder="Project Inquiry" required />
                {errors.subject && <ErrorText>{errors.subject}</ErrorText>}

                <FloatingTextarea label="Message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell me about your project..." required />
                {errors.message && <ErrorText>{errors.message}</ErrorText>}

                <SubmitBtn type="submit" disabled={isSubmitting}>
                  {isSubmitting ? '⏳ Sending...' : 'Send Message'}
                </SubmitBtn>
              </>
            )}
          </FormPanel>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;